import { useCallback, useEffect, useRef, useState } from 'react';
import { normalizeResponseBlocks } from '../structured/structuredBlockUtils';
import { StreamChunkAccumulator } from './streamChunkAccumulator';
import {
    buildStreamSegments,
    resolveStreamingStructuredState,
} from './streamCompletionDetector';

const DEFAULT_CHARS_PER_FRAME = 8;
const IDLE_STATE = {
    active: false,
    phase: 'idle',
    displayContent: '',
    completedBlocks: [],
    pendingMarkdown: '',
    responseBlocks: [],
    isComplete: false,
};

export function useStreamingResponse({ charsPerFrame = DEFAULT_CHARS_PER_FRAME } = {}) {
    const accumulatorRef = useRef(new StreamChunkAccumulator());
    const segmentsRef = useRef([]);
    const rafRef = useRef(null);

    const [state, setState] = useState(IDLE_STATE);

    const cancel = useCallback(() => {
        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
        accumulatorRef.current.reset();
        segmentsRef.current = [];
        setState(IDLE_STATE);
    }, []);

    const begin = useCallback(() => {
        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
        accumulatorRef.current.reset();
        segmentsRef.current = [];
        setState({
            active: true,
            phase: 'pending',
            displayContent: '',
            completedBlocks: [],
            pendingMarkdown: '',
            responseBlocks: [],
            isComplete: false,
        });
    }, []);

    const streamResponse = useCallback(
        data =>
            new Promise(resolve => {
                const answer = String(data?.answer ?? data?.final_answer ?? '');
                const blocks = data?.response_blocks ?? data?.blocks ?? [];
                const segments = buildStreamSegments(answer, blocks);
                segmentsRef.current = segments;

                const fullText = segments.map(segment => segment.text).join('');
                accumulatorRef.current.setFull(fullText);

                let revealed = 0;
                const totalLength = fullText.length;

                const publish = (phase, isComplete) => {
                    const displayContent = accumulatorRef.current.getDisplayContent();
                    const structured = resolveStreamingStructuredState(segments, displayContent);
                    setState({
                        active: true,
                        phase,
                        displayContent,
                        completedBlocks: isComplete
                            ? normalizeResponseBlocks(blocks)
                            : structured.completedBlocks,
                        pendingMarkdown: isComplete ? '' : structured.pendingMarkdown,
                        responseBlocks: blocks,
                        isComplete,
                    });
                };

                const tick = () => {
                    revealed = Math.min(revealed + charsPerFrame, totalLength);
                    accumulatorRef.current.revealTo(revealed);
                    publish('streaming', false);

                    if (revealed < totalLength) {
                        rafRef.current = requestAnimationFrame(tick);
                        return;
                    }

                    accumulatorRef.current.revealTo(totalLength);
                    publish('complete', true);
                    rafRef.current = null;
                    resolve(data);
                };

                if (!totalLength) {
                    publish('complete', true);
                    resolve(data);
                    return;
                }

                rafRef.current = requestAnimationFrame(tick);
            }),
        [charsPerFrame],
    );

    const appendChunk = useCallback(chunk => {
        accumulatorRef.current.append(chunk);
        accumulatorRef.current.revealTo(accumulatorRef.current.getFullContent().length);
        const displayContent = accumulatorRef.current.getDisplayContent();
        const structured = resolveStreamingStructuredState(segmentsRef.current, displayContent);
        setState(prev => ({
            ...prev,
            active: true,
            phase: prev.phase === 'idle' ? 'streaming' : prev.phase,
            displayContent,
            completedBlocks: structured.completedBlocks,
            pendingMarkdown: structured.pendingMarkdown,
        }));
    }, []);

    useEffect(
        () => () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        },
        [],
    );

    return {
        state,
        begin,
        streamResponse,
        appendChunk,
        cancel,
    };
}
